import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Pencil, RefreshCw, Search, Trash2 } from "lucide-react";
import {
  keysApi,
  distributionsApi,
  suppliersApi,
  usageApi,
  mainTemplatesApi,
  relaySitesApi,
  pushSitesApi,
  type APIKey,
  type TaskStatus,
  ApiError,
  RequestTimeoutError,
} from "@/api/client";
import { opsSupplierApi } from "@/api/opsSupplierClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { OperatorUsageCell } from "@/components/usage/OperatorUsageCell";
import { Pagination } from "@/components/Pagination";

// ---------------------------------------------------------------------------
// /keys — 所有 Key · 明细视图(原型见 <repo>/demo-key.html)
//
// 只读明细列表 + 行内操作。搜索 / 状态 / 服务商 / 时间筛选,客户端分页。
// "最近分发" 由 distributionsApi.list() 按 key_id 归并出每个 Key 的最新任务。
// 「→」深链到 /tasks/list?key_id=。Key 新建已移至 /distribute,本页不再提供。
// ---------------------------------------------------------------------------

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const TIME_WINDOWS = [
  { key: "all", label: "全部", ms: 0 },
  { key: "1h", label: "1h", ms: 3600_000 },
  { key: "24h", label: "24h", ms: 86_400_000 },
  { key: "7d", label: "7d", ms: 604_800_000 },
];

const STATUS_LABEL: Record<APIKey["status"], string> = {
  active: "已启用",
  inactive: "已停用",
  expired: "已过期",
};
const STATUS_VARIANT: Record<APIKey["status"], "success" | "muted" | "destructive"> = {
  active: "success",
  inactive: "muted",
  expired: "destructive",
};

function dotClass(status: TaskStatus | null): string {
  switch (status) {
    case "success":
      return "bg-green-500";
    case "failed":
      return "bg-red-500";
    case "dispatching":
    case "pending":
      return "bg-amber-500";
    default:
      return "bg-slate-300";
  }
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diff = Date.now() - then;
  if (diff < 0) return "刚刚";
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s} 秒前`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} 分钟前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} 小时前`;
  return `${Math.floor(h / 24)} 天前`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const inpCls = "h-9 rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring";

export function KeysPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { key_id: keyIdFilter } = useSearch({ strict: false }) as { key_id?: number };

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | APIKey["status"]>("all");
  const [providerFilter, setProviderFilter] = useState("all");
  const [ownerSupplierFilter, setOwnerSupplierFilter] = useState("all");
  const [timeWin, setTimeWin] = useState("all");
  const [templateFilter, setTemplateFilter] = useState("");
  const [siteFilter, setSiteFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [editing, setEditing] = useState<APIKey | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => { setPage(1); }, [keyIdFilter]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const createdAfter = useMemo(() => {
    const win = TIME_WINDOWS.find((w) => w.key === timeWin)?.ms ?? 0;
    return win > 0 ? new Date(Date.now() - win).toISOString() : undefined;
  }, [timeWin]);
  const keysQ = useQuery({
    queryKey: ["keys", "page", page, pageSize, debouncedSearch, statusFilter, providerFilter, ownerSupplierFilter, timeWin, keyIdFilter, templateFilter, siteFilter],
    queryFn: () => keysApi.list({
      page,
      page_size: pageSize,
      include_supplier_fields: true,
      include_task_counts: true,
      ...(keyIdFilter ? { ids: [keyIdFilter] } : {}),
      ...(templateFilter ? { template_id: Number(templateFilter) } : {}),
      ...(siteFilter ? { site_type: siteFilter.split(":")[0] as "relay" | "push", site_id: Number(siteFilter.split(":")[1]) } : {}),
      ...(debouncedSearch ? { q: debouncedSearch } : {}),
      ...(statusFilter !== "all" ? { status: statusFilter } : {}),
      ...(providerFilter !== "all" ? { supplier_field: providerFilter } : {}),
      ...(ownerSupplierFilter !== "all" ? { owner_supplier_id: Number(ownerSupplierFilter) } : {}),
      ...(createdAfter ? { created_after: createdAfter } : {}),
    }),
    placeholderData: keepPreviousData,
  });
  const suppliersQ = useQuery({
    queryKey: ["ops-suppliers", "key-owner-filter"],
    queryFn: () => opsSupplierApi.list({ page: 1, page_size: 100 }),
    staleTime: 60_000,
  });
  const templatesQ = useQuery({ queryKey: ["main-templates"], queryFn: () => mainTemplatesApi.list() });
  const relaySitesQ = useQuery({ queryKey: ["relay-sites"], queryFn: relaySitesApi.list });
  const pushSitesQ = useQuery({ queryKey: ["push-sites"], queryFn: pushSitesApi.list });
  const providers = keysQ.data?.supplier_fields ?? [];
  const total = keysQ.data?.total ?? 0;
  const pageItems = keysQ.isPlaceholderData ? [] : keysQ.data?.items ?? [];
  const pageKeyIDs = useMemo(() => pageItems.map((item) => item.id), [pageItems]);
  const tasksQ = useQuery({
    queryKey: ["tasks", "latest-for-keys", pageKeyIDs],
    queryFn: () => distributionsApi.list({
      key_ids: pageKeyIDs,
      latest_per_key: true,
      page: 1,
      page_size: Math.max(1, pageKeyIDs.length),
    }),
    enabled: pageKeyIDs.length > 0,
  });
  const latestByKey = useMemo(() => {
    const m = new Map<number, { status: TaskStatus; at: string | null }>();
    for (const task of tasksQ.data?.items ?? []) {
      m.set(task.key_id, { status: task.status, at: task.dispatched_at ?? task.created_at });
    }
    return m;
  }, [tasksQ.data]);
  const usageQ = useQuery({
    queryKey: ["channel-usage-summaries", pageKeyIDs],
    queryFn: () => usageApi.listChannelSummaries(pageKeyIDs),
    enabled: pageKeyIDs.length > 0,
  });
  const usageByKey = useMemo(
    () => new Map((usageQ.data?.items ?? []).map((item) => [item.key_id, item])),
    [usageQ.data]
  );

  const deleteMut = useMutation({
    mutationFn: (id: number) => keysApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["keys"] });
      setEditing(null);
      setMsg("已删除");
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : "删除失败"),
  });

  const refreshUsageMut = useMutation({
    mutationFn: ({ keyIds }: { keyIds: number[]; scope: "page" | "row" }) => usageApi.refresh(keyIds),
    onSuccess: async (result) => {
      await usageQ.refetch();
      if (result.source_count === 0) {
        setMsg(`刷新完成：${result.key_count} 个渠道尚无成功分发目标`);
      } else if (result.failed_count > 0 || result.partial_count > 0) {
        setMsg(
          `刷新完成（部分异常）：成功 ${result.success_count}，部分 ${result.partial_count}，失败 ${result.failed_count}`
        );
      } else {
        setMsg(`刷新完成：${result.key_count} 个渠道、${result.source_count} 个分发目标`);
      }
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        setMsg("已有用量刷新正在进行，请稍后再试");
        return;
      }
      if (error instanceof RequestTimeoutError) {
        setMsg(error.message);
        return;
      }
      setMsg(error instanceof ApiError ? error.message : "用量刷新失败");
    },
  });

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">所有 Key</h1>
        <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">明细视图</span>
        <div className="ml-auto flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={pageKeyIDs.length === 0 || refreshUsageMut.isPending}
            onClick={() => {
              setMsg(null);
              refreshUsageMut.mutate({ keyIds: pageKeyIDs, scope: "page" });
            }}
          >
            <RefreshCw
              className={`mr-1.5 h-3.5 w-3.5 ${
                refreshUsageMut.isPending && refreshUsageMut.variables?.scope === "page" ? "animate-spin" : ""
              }`}
            />
            {refreshUsageMut.isPending && refreshUsageMut.variables?.scope === "page" ? "刷新本页中…" : "刷新本页用量"}
          </Button>
          <button
            type="button"
            onClick={() => {
              keysQ.refetch();
              if (pageKeyIDs.length > 0) {
                tasksQ.refetch();
                usageQ.refetch();
              }
              templatesQ.refetch();
              relaySitesQ.refetch();
              pushSitesQ.refetch();
            }}
            className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-accent"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${keysQ.isFetching || tasksQ.isFetching ? "animate-spin" : ""}`} />
            手动刷新列表
          </button>
        </div>
      </div>

      {msg && <div className="text-sm text-muted-foreground">{msg}</div>}
      {keysQ.isError && <div className="text-sm text-destructive">Key 列表加载失败：{keysQ.error instanceof Error ? keysQ.error.message : "未知错误"}</div>}
      {(tasksQ.isError || templatesQ.isError || relaySitesQ.isError || pushSitesQ.isError) && <div className="text-sm text-destructive">部分关联数据加载失败，请刷新重试。</div>}
      {keyIdFilter && <div className="text-xs text-primary">当前 Key ID：{keyIdFilter} <button type="button" className="underline" onClick={() => { setPage(1); navigate({ to: "/keys", search: {} }); }}>清除过滤</button></div>}

      {/* Toolbar */}
      <div className="rounded-xl border bg-card p-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[160px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              className={`${inpCls} w-full pl-7`}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="搜索 Key 名称…"
            />
          </div>
          <select className={inpCls} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as typeof statusFilter); setPage(1); }}>
            <option value="all">全部状态</option>
            <option value="active">已启用</option>
            <option value="inactive">已停用</option>
            <option value="expired">已过期</option>
          </select>
          <select className={inpCls} value={providerFilter} onChange={(e) => { setProviderFilter(e.target.value); setPage(1); }}>
            <option value="all">全部服务商</option>
            {providers.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <select className={inpCls} value={ownerSupplierFilter} onChange={(e) => { setOwnerSupplierFilter(e.target.value); setPage(1); }}>
            <option value="all">全部归属</option>
            {(suppliersQ.data?.items ?? []).map((supplier) => (
              <option key={supplier.id} value={supplier.id}>{supplier.username}</option>
            ))}
          </select>
          <select aria-label="筛选主模板" className={inpCls} value={templateFilter} onChange={(e) => { setTemplateFilter(e.target.value); setPage(1); }}>
            <option value="">全部主模板</option>
            {(templatesQ.data?.items ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select aria-label="筛选目标站点" className={inpCls} value={siteFilter} onChange={(e) => { setSiteFilter(e.target.value); setPage(1); }}>
            <option value="">全部目标站点</option>
            <optgroup label="中转站">{(relaySitesQ.data?.items ?? []).map((s) => <option key={s.id} value={`relay:${s.id}`}>{s.name}</option>)}</optgroup>
            <optgroup label="推送站">{(pushSitesQ.data?.items ?? []).map((s) => <option key={s.id} value={`push:${s.id}`}>{s.name}</option>)}</optgroup>
          </select>
          <span className="mx-1 text-xs text-muted-foreground">|</span>
          {TIME_WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              onClick={() => { setTimeWin(w.key); setPage(1); }}
              className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                timeWin === w.key ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between pt-2 text-xs text-muted-foreground">
          <span>共 {total} 条{total > pageSize ? ` · 当前显示 ${pageItems.length} 条` : ""}</span>
          <span>按创建时间 ↓</span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-left text-xs font-medium text-muted-foreground">
              <tr>
                <th className="px-3 py-2.5">名称</th>
                <th className="px-3 py-2.5">服务商</th>
                <th className="px-3 py-2.5">归属</th>
                <th className="px-3 py-2.5">状态</th>
                <th className="px-3 py-2.5">用量</th>
                <th className="px-3 py-2.5">最近分发</th>
                <th className="px-3 py-2.5">关联任务</th>
                <th className="px-3 py-2.5">创建时间</th>
                <th className="px-3 py-2.5 text-center">操作</th>
              </tr>
            </thead>
            <tbody>
              {(keysQ.isLoading || keysQ.isPlaceholderData) && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">加载中…</td></tr>
              )}
              {!keysQ.isLoading && !keysQ.isError && !keysQ.isPlaceholderData && pageItems.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-10 text-center text-muted-foreground">暂无匹配的 Key</td></tr>
              )}
              {pageItems.map((k) => {
                const latest = latestByKey.get(k.id) ?? null;
                return (
                  <tr key={k.id} className="border-b hover:bg-muted/30">
                    <td className="px-3 py-2.5">
                      <div className="font-medium">{k.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">
                        #{k.id} · Key {k.key_hint || "末四位未知"}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">{k.supplier_field || "—"}</td>
                    <td className="px-3 py-2.5 text-xs">
                      {k.owner_type === "supplier" ? (
                        <span title={`Supplier #${k.owner_supplier_id ?? "—"}`}>{k.owner_supplier_username || `Supplier #${k.owner_supplier_id}`}</span>
                      ) : <span className="text-muted-foreground">平台自有</span>}
                    </td>
                    <td className="px-3 py-2.5"><Badge variant={STATUS_VARIANT[k.status]}>{STATUS_LABEL[k.status]}</Badge></td>
                    <td className="px-3 py-2.5">
                      <OperatorUsageCell
                        summary={usageByKey.get(k.id)}
                        loading={usageQ.isLoading}
                        failed={usageQ.isError}
                        refreshing={
                          refreshUsageMut.isPending &&
                          refreshUsageMut.variables?.scope === "row" &&
                          refreshUsageMut.variables.keyIds[0] === k.id
                        }
                        refreshDisabled={refreshUsageMut.isPending}
                        onRefresh={() => {
                          setMsg(null);
                          refreshUsageMut.mutate({ keyIds: [k.id], scope: "row" });
                        }}
                      />
                    </td>
                    <td className="px-3 py-2.5 text-xs">
                      <span className={`mr-1.5 inline-block h-2 w-2 rounded-full ${dotClass(latest?.status ?? null)}`} />
                      {latest ? relativeTime(latest.at) : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-xs whitespace-nowrap">
                      <button type="button" className="text-primary hover:underline" onClick={() => navigate({ to: "/relay/tasks/list", search: { key_id: k.id } })}>中转 {k.relay_task_count ?? "—"}</button>
                      <span className="mx-1 text-muted-foreground">/</span>
                      <button type="button" className="text-primary hover:underline" onClick={() => navigate({ to: "/push/tasks/list", search: { key_id: k.id } })}>推送 {k.push_task_count ?? "—"}</button>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">{formatDateTime(k.created_at)}</td>
                    <td className="px-3 py-2.5 text-center">
                      <button
                        type="button"
                        className="rounded p-1 text-muted-foreground hover:bg-accent"
                        onClick={() => { setEditing(k); setMsg(null); }}
                        title="编辑"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <Pagination
          className="px-4 pb-3"
          page={page}
          total={keysQ.data?.total}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          loading={keysQ.isFetching}
          pageItemCount={keysQ.isPlaceholderData ? undefined : pageItems.length}
        />
      </div>

      <div className="text-xs text-muted-foreground">点击中转或推送任务数，查看该 Key 对应站点类型的全部任务。</div>

      {editing && (
        <EditKeyModal
          k={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ["keys"] }); setEditing(null); setMsg("已保存"); }}
          onDelete={() => {
            if (confirm(`确认软删「${editing.name}」?历史分发任务保留,只标记本条删除。\n如需停用下游所有渠道,请到该站点页手动执行「禁用渠道」。`)) {
              deleteMut.mutate(editing.id);
            }
          }}
          deleting={deleteMut.isPending}
        />
      )}
    </div>
  );
}

function EditKeyModal({
  k,
  onClose,
  onSaved,
  onDelete,
  deleting,
}: {
  k: APIKey;
  onClose: () => void;
  onSaved: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const [form, setForm] = useState({
    name: k.name,
    status: k.status,
    base_url: k.base_url,
    models: k.models,
    unit_price: k.unit_price,
    supplier_field: k.supplier_field,
    protocol_type: k.protocol_type,
    remark: k.remark,
  });
  const [err, setErr] = useState<string | null>(null);

  const suppliersQ = useQuery({
    queryKey: ["suppliers"],
    queryFn: suppliersApi.list,
    staleTime: 60_000,
  });
  const protocols = suppliersQ.data?.items ?? [];

  const saveMut = useMutation({
    mutationFn: () => {
      // protocol_type 可显式置 null(后台 keyUpdateReq.ProtocolType *int),
      // 0 也写出去(后端允许存 0,只是分发时 dispatcher fail-fast)。
      const pt: number | null = form.protocol_type === null ? null : Number(form.protocol_type);
      if (pt !== null && (!Number.isInteger(pt) || pt < 0)) {
        throw new Error("协议类型必须是正整数或留空");
      }
      return keysApi.update(k.id, {
        name: form.name.trim(),
        status: form.status,
        base_url: form.base_url.trim(),
        models: form.models.trim(),
        unit_price: Number(form.unit_price),
        supplier_field: form.supplier_field.trim(),
        protocol_type: pt,
        remark: form.remark.trim(),
      });
    },
    onSuccess: onSaved,
    onError: (e) => setErr(e instanceof Error ? e.message : "保存失败"),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl border bg-card p-5 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <h2 className="mb-4 text-base font-semibold">编辑 Key「{k.name}」</h2>
        {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">{err}</div>}
        <form
          className="grid grid-cols-2 gap-3"
          onSubmit={(e) => { e.preventDefault(); setErr(null); saveMut.mutate(); }}
        >
          <div className="col-span-2 space-y-1">
            <Label>名称</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div className="space-y-1">
            <Label>状态</Label>
            <select className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as APIKey["status"] })}>
              <option value="active">已启用</option>
              <option value="inactive">已停用</option>
              <option value="expired">已过期</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label>批发单价</Label>
            <Input type="number" step="0.0001" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: Number(e.target.value) })} />
          </div>
          <div className="col-span-2 space-y-1">
            <Label>Base URL</Label>
            <Input value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} placeholder="https://api.openai.com/v1" />
          </div>
          <div className="col-span-2 space-y-1">
            <Label>模型列表(逗号分隔)</Label>
            <Input value={form.models} onChange={(e) => setForm({ ...form, models: e.target.value })} placeholder="gpt-4o,gpt-4o-mini" />
          </div>
          <div className="space-y-1">
            <Label>供应商</Label>
            <Input value={form.supplier_field} onChange={(e) => setForm({ ...form, supplier_field: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label>
              协议类型(protocol_type)
              {form.protocol_type === null && (
                <span className="ml-1 text-[11px] text-muted-foreground">— 未设置,分发时如未绑模板会失败</span>
              )}
            </Label>
            <div className="flex gap-2">
              <select
                className={inpCls + " flex-1"}
                value={form.protocol_type ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    protocol_type: e.target.value === "" ? null : Number(e.target.value),
                  })
                }
              >
                <option value="">— 未设置(分发将失败)—</option>
                {protocols.map((p) => (
                  <option key={p.type} value={p.type}>
                    {p.name} ({p.type})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-1">
            <Label>备注</Label>
            <Input value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} />
          </div>
          <div className="col-span-2 mt-2 flex items-center justify-between">
            <Button type="button" variant="destructive" size="sm" onClick={onDelete} disabled={deleting}>
              <Trash2 className="mr-1 h-4 w-4" /> 删除
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>取消</Button>
              <Button type="submit" disabled={saveMut.isPending}>{saveMut.isPending ? "保存中…" : "保存"}</Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
